import React from 'react';
import PropTypes from 'prop-types';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import {styled} from '@mui/system';

import {COMPLETION_MIN} from '../settings';
import {postCall} from '../helpers/request';


const StyledAutocompleteRoot = styled('div')({
    flexGrow: 1
});

const StyledDivider = styled('div')({
    height: 0
});


const renderInputComponent = (inputProps) => {
    const { css_class, inputRef = () => {}, ref, ...other } = inputProps;

    return (
        <TextField
            fullWidth
            slotProps={{
                classes: {
                    input: css_class,
                }
            }}
            {...other}
        />
    );
};

const renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const matches = match(suggestion.label, query);
    const parts = parse(suggestion.label, matches);

    return (
        <MenuItem selected={isHighlighted} component="div">
            <div>
                {parts.map((part, index) => (
                    <span key={index} style={{ fontWeight: part.highlight ? 500 : 400 }}>{part.text}</span>
                ))}
            </div>
        </MenuItem>
    );
};

const getSuggestionValue = (suggestion) => {
    return suggestion.label;
};


class Autocomplete extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            anchorEl: null,
            currentValue: this.props.value || '',
            suggestions: [],
            valid_value: false
        };

        this.is_mounted = false;

        this.styles = {
            container: {
                position: 'relative',
                padding: 0,
                margin: 0,
                height: 25
            },
            suggestionsContainerOpen: {
                position: 'absolute',
                marginTop: 0,
                left: 0,
                zIndex: 1000
            },
            suggestion: {
                display: 'block'
            },
            suggestionsList: {
                margin: 0,
                padding: 0,
                listStyleType: 'none'
            }
        };
    }

    componentDidMount(){
        this.is_mounted = true;
    }

    componentWillUnmount(){
        this.is_mounted = false;
    }

    autoCompleteSearchFailed = () => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        this.props.autoCompleteSearchFailedCallback();
    };

    autoCompleteSearchDone = (data) => {
        if(!this.is_mounted) {
            // skip updating component if not mounted to avoid.
            return;
        }

        if (data && data.suggestions){
            this.setState({suggestions: data.suggestions});
        }
    };

    autoCompleteSearch = (search_string) => {
        if (search_string.length >= COMPLETION_MIN){
            postCall(this.props.getSuggestionsUrl, {search_string: search_string}, this.autoCompleteSearchDone, this.autoCompleteSearchFailed);
        }
    };

    handleSuggestionsFetchRequested = ({ value }) => {
        this.autoCompleteSearch(value);
    };

    handleSuggestionsClearRequested = () => {
        this.setState({suggestions:[]});
    };

    handleChange = (event, { newValue }) => {
        this.setState({
            currentValue: newValue,
            valid_value: false
        });
        if(this.props.onChange){
            this.props.onChange();
        }
    };

    handleSelection = (event, { suggestion }) => {
        this.setState({
            valid_value: true
        });
        this.props.onSuggestionSelected(suggestion);
    };

    checkRequiredSelection = () => {
        // check if a suggestion was selected
        if (this.state.currentValue && !this.state.valid_value){
            this.setState({
                currentValue: ''
            });
            this.props.selectionRequiredCallback();
        }
    };

    render(){
        const autoSuggestProps = {
            renderInputComponent,
            suggestions: this.state.suggestions,
            onSuggestionsFetchRequested: this.handleSuggestionsFetchRequested,
            onSuggestionsClearRequested: this.handleSuggestionsClearRequested,
            onSuggestionSelected: this.handleSelection,
            getSuggestionValue,
            renderSuggestion,
        };

        let inputProps = {
            id: this.props.id,
            variant: this.props.variant || 'outlined',
            label: this.props.label,
            placeholder: this.props.placeholder || null,
            value: this.state.currentValue,
            error: this.props.error || false,
            size: this.props.small_size ? 'small' : 'medium',
            onChange: this.handleChange,
        };

        let theme = {
            container: this.styles.container,
            suggestionsContainerOpen: this.styles.suggestionsContainerOpen,
            suggestionsList: this.styles.suggestionsList,
            suggestion: this.styles.suggestion,
        };


        if(this.props.onBlur){
            inputProps['onBlur'] = this.props.onBlur;
        }

        if(this.props.selectionRequired && this.props.selectionRequiredCallback) {
            inputProps['onBlur'] = this.checkRequiredSelection;
        }

        return (
            <StyledAutocompleteRoot>
                <Autosuggest
                    {...autoSuggestProps}
                    inputProps={inputProps}
                    theme={theme}
                    renderSuggestionsContainer={options => (
                        <Paper {...options.containerProps} square>
                            {options.children}
                        </Paper>
                    )}
                />
                <StyledDivider />
            </StyledAutocompleteRoot>
        );
    }

}


Autocomplete.propTypes = {
    error: PropTypes.bool,
    variant: PropTypes.string,
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    value: PropTypes.string,
    placeholder: PropTypes.string,
    small_size: PropTypes.bool,
    onBlur: PropTypes.func,
    onSuggestionSelected: PropTypes.func,
    onChange: PropTypes.func,
    getSuggestionsUrl: PropTypes.string,
    selectionRequired: PropTypes.bool,
    selectionRequiredCallback: PropTypes.func,
    autoCompleteSearchFailedCallback: PropTypes.func
};


export default Autocomplete;
